'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Bell, Check, ChevronLeft, X } from 'lucide-react';
import { Badge, Button } from '@/components/ds';
import {
  NotificationRow,
  type NotificationRowData,
} from '@/components/notifications/NotificationRow';
import { useUnreadNotifications } from '@/components/providers/NotificationsProvider';
import {
  isUnread,
  markAllNotificationsRead,
  markNotificationsRead,
} from '@/lib/notifications';
import { cn } from '@/lib/utils';

// Only navigate for internal app paths; ignore absolute/external URLs
// (http://, https://, or protocol-relative //host).
function isInternalPath(url?: string | null): url is string {
  return !!url && url.startsWith('/') && !url.startsWith('//');
}

interface NotificationActor {
  id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

interface Notification {
  id: string | number;
  type: string;
  title: string;
  message: string | null;
  metadata?: Record<string, unknown> | null;
  actor?: NotificationActor | null;
  action_url?: string | null;
  created_at: string;
  read: boolean | null;
}

// Map an API row onto the shared row's view model. The dropdown and the
// /notifications page feed the same component from the same endpoint.
function toRowData(n: Notification): NotificationRowData {
  const actor = n.actor || null;
  return {
    id: String(n.id),
    type: n.type || '',
    title: n.title || '',
    message: n.message,
    actor: actor
      ? {
          name: actor.display_name || actor.username || 'Someone',
          username: actor.username,
          avatarUrl: actor.avatar_url,
        }
      : null,
    actionUrl: n.action_url,
    createdAt: n.created_at,
    read: n.read,
  };
}

interface NotificationCenterProps {
  userId?: string | null;
  isProfileComplete?: boolean;
  trackEvent?: (event: string, properties?: Record<string, any>) => void;
}

export default function NotificationCenter({ userId, isProfileComplete, trackEvent }: NotificationCenterProps) {
  const router = useRouter();
  // The unread badge count comes from the single app-wide source; this component
  // no longer derives its own count from the fetched list.
  const {
    unreadCount,
    refresh: refreshUnread,
    onChange: onNotificationsChange,
  } = useUnreadNotifications();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const fetchNotifications = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    fetch(`/api/notifications?userId=${userId}`, { credentials: 'include' })
      .then(async (response) => {
        if (response.ok) {
          // The API returns an object: { notifications, unread_count }.
          // (Guard for a bare array too, in case of older responses.)
          const data = await response.json();
          const list: Notification[] = Array.isArray(data)
            ? data
            : data?.notifications || [];
          setNotifications(list);
        } else {
          console.error('Failed to fetch notifications');
          setNotifications([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching notifications:', error);
        setNotifications([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    if (userId) fetchNotifications();
  }, [userId, fetchNotifications]);

  // Keep an OPEN dropdown live off the provider's existing realtime fan-out —
  // the same signal the /notifications page consumes. Previously the badge
  // ticked up behind an open dropdown whose list never changed. No new channel:
  // the provider owns the only notifications subscription.
  useEffect(() => {
    if (!userId || !isOpen) return;
    return onNotificationsChange(() => {
      fetchNotifications();
    });
  }, [userId, isOpen, onNotificationsChange, fetchNotifications]);

  // Purely client-side, over the rows already loaded. No extra query, no second
  // count source — the tab's N is the provider's unread value.
  const visible = useMemo(
    () => (filter === 'unread' ? notifications.filter((n) => isUnread(n.read)) : notifications),
    [notifications, filter]
  );

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    markAllNotificationsRead()
      .then(() => refreshUnread())
      .catch(() => fetchNotifications());
  };

  // Don't render if user is not authenticated or doesn't have a complete profile
  if (!userId || !isProfileComplete) {
    return null;
  }

  const handleNotificationClick = (row: NotificationRowData) => {
    trackEvent?.('notification_clicked', {
      notification_type: row.type,
      notification_id: row.id,
    });

    // Optimistically mark read and persist (real columns: read + read_at,
    // handled server-side). Fire-and-forget — a failed mark must not block nav.
    if (isUnread(row.read)) {
      setNotifications((prev) =>
        prev.map((n) => (String(n.id) === row.id ? { ...n, read: true } : n))
      );
      // Keep the global badge in sync with the read we just persisted. A failed
      // mark must not block navigation.
      markNotificationsRead([row.id])
        .then(() => refreshUnread())
        .catch(() => {});
    }

    setIsOpen(false);
    if (isInternalPath(row.actionUrl)) {
      router.push(row.actionUrl);
    }
  };

  const close = () => setIsOpen(false);

  const tabClass = (active: boolean) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-sm px-2.5 h-7 text-xs font-medium transition-colors',
      active
        ? 'bg-accent text-text-on-accent'
        : 'bg-surface-3 text-text-secondary border border-border-subtle hover:bg-surface-2 hover:text-text-primary'
    );

  const list = loading ? (
    <div className="p-6 text-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent mx-auto" />
    </div>
  ) : visible.length === 0 ? (
    <div className="p-6 text-center">
      <Bell className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
      <p className="text-text-tertiary text-sm">
        {filter === 'unread' ? 'Nothing unread' : 'No notifications'}
      </p>
    </div>
  ) : (
    <div className="divide-y divide-border-subtle">
      {visible.map((notification) => {
        const row = toRowData(notification);
        return (
          <NotificationRow
            key={row.id}
            notification={row}
            surface="surface-2"
            clickable={isInternalPath(row.actionUrl)}
            onSelect={handleNotificationClick}
          />
        );
      })}
    </div>
  );

  const header = (mobile: boolean) => (
    <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-3 py-2">
      <div className="flex items-center gap-1">
        {mobile && (
          <button
            type="button"
            onClick={close}
            aria-label="Back"
            className="-ml-1 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
          </button>
        )}
        <span className="text-text-primary text-base font-semibold">Notifications</span>
      </div>
      <div className="flex items-center gap-1">
        {unreadCount > 0 && (
          <Button variant="ghost" size="sm" onClick={markAllAsRead}>
            <Check className="w-3.5 h-3.5" strokeWidth={1.75} />
            <span className="text-xs">Mark all read</span>
          </Button>
        )}
        {!mobile && (
          <button
            type="button"
            onClick={close}
            aria-label="Close notifications"
            className="flex items-center justify-center rounded-sm text-text-tertiary hover:text-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  const tabs = (
    <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
      <button type="button" onClick={() => setFilter('all')} className={tabClass(filter === 'all')}>
        All
      </button>
      <button
        type="button"
        onClick={() => setFilter('unread')}
        className={tabClass(filter === 'unread')}
      >
        Unread
        <Badge
          count={unreadCount}
          tone={filter === 'unread' ? 'soft' : 'accent'}
          className={filter === 'unread' ? 'bg-text-on-accent text-accent' : undefined}
        />
      </button>
    </div>
  );

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          // Refresh the list each time the dropdown opens.
          if (next) fetchNotifications();
        }}
        className="relative text-text-secondary hover:text-text-primary"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <Badge count={unreadCount} tone="accent" cap={9} className="absolute -top-1 -right-1" />
        )}
      </Button>

      {isOpen && (
        <>
          {/* Desktop: anchored dropdown. */}
          <div className="hidden md:block absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-1.5rem)] rounded-lg border border-border-subtle bg-surface-2 shadow-xl z-50 overflow-hidden">
            {header(false)}
            {tabs}
            <div className="max-h-[70vh] overflow-y-auto scrollbar-thin-themed">{list}</div>
          </div>

          {/* Mobile: full-height sheet, same rows. */}
          <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-surface-1">
            {header(true)}
            {tabs}
            <div className="flex-1 overflow-y-auto scrollbar-thin-themed bg-surface-2">{list}</div>
          </div>
        </>
      )}

      {isOpen && (
        <div
          className="hidden md:block fixed inset-0 z-40"
          onClick={close}
        />
      )}
    </div>
  );
}
